import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { SITE } from "@/lib/site";

const EFFECTIVE_DATE = "18 June 2025";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: [
      `NexaVerve Solutions ("we", "us", "our") is a facilities and compliance consultancy based in Manchester, United Kingdom, operating the website nexaverve.uk. We are the "data controller" for any personal information you provide to us through this site.`,
      `If you have any questions about this policy or the way we handle your data, please contact us using the details in section 10 below.`,
    ],
  },
  {
    id: "information",
    title: "2. Information we collect",
    body: [
      "When you use our website or contact us, we may collect the following personal information:",
    ],
    list: [
      "Your name (from the contact form).",
      "Your email address (from the contact form or if you email us).",
      "Your phone number (optional field on the contact form, or if you call/WhatsApp us).",
      "The service you are enquiring about and the message you send us.",
      "Basic technical information your browser sends when visiting the site (e.g. approximate location, browser type). We do not run analytics tracking on this website.",
    ],
  },
  {
    id: "use",
    title: "3. How we use your information",
    body: ["We use the information you provide to:"],
    list: [
      "Respond to your enquiry and provide the services you have asked about.",
      "Prepare quotes, proposals or contractual documentation.",
      "Contact you about the progress of any work we do for you.",
      "Keep records required for compliance, audit and tax purposes.",
    ],
  },
  {
    id: "legal-basis",
    title: "4. Legal basis for processing",
    body: [
      `Under UK GDPR, we rely on the following lawful bases for processing your personal information:`,
    ],
    list: [
      "Consent — where you have voluntarily submitted the contact form or emailed us.",
      "Legitimate interests — to respond to enquiries, run our business and improve our services.",
      "Contractual necessity — where we need to process data to prepare or perform a contract you have with us.",
      "Legal obligation — where we are required to retain records (e.g. accounting, insurance, compliance).",
    ],
  },
  {
    id: "sharing",
    title: "5. Who we share your information with",
    body: [
      "We do not sell your personal information. We only share it where necessary to run our business, including with:",
    ],
    list: [
      "Our email provider (Fasthosts) to receive and reply to enquiries.",
      "Our website hosting provider, which stores contact-form submissions in a secure database.",
      "Vetted subcontractors we engage to deliver a service you have requested (limited to what they need to do the job).",
      "Regulators, auditors or authorities where we are legally required to do so.",
    ],
  },
  {
    id: "retention",
    title: "6. How long we keep your information",
    body: [
      "We keep enquiry data for as long as it is reasonably needed to respond and to keep a record of the conversation — typically up to 24 months from the last contact.",
      "Where you become a client, we keep client records for the duration of the engagement and for up to 7 years after it ends, in line with UK tax and compliance requirements.",
    ],
  },
  {
    id: "security",
    title: "7. Security",
    body: [
      "We take reasonable technical and organisational measures to protect your information from unauthorised access, loss or disclosure. Our website is served over HTTPS, and enquiry data is stored in a secured database with restricted access.",
      "No online service can be guaranteed 100% secure, but we work hard to minimise risk and will notify you and the ICO if there is a personal data breach that affects your rights.",
    ],
  },
  {
    id: "rights",
    title: "8. Your rights",
    body: ["Under UK GDPR you have the right to:"],
    list: [
      "Ask us for a copy of the personal information we hold about you.",
      "Ask us to correct information you believe is inaccurate.",
      "Ask us to erase your personal information (where we have no legal reason to keep it).",
      "Ask us to restrict or object to how we use your information.",
      "Withdraw your consent at any time (where consent is the lawful basis).",
      "Lodge a complaint with the UK Information Commissioner's Office (ICO) at ico.org.uk.",
    ],
    body_after: [
      "To exercise any of these rights, please email us at hello@nexaverve.uk. We will respond within one month.",
    ],
  },
  {
    id: "cookies",
    title: "9. Cookies and tracking",
    body: [
      "This website does not use marketing cookies or third-party analytics trackers. We load web fonts from Google Fonts and Fontshare so the site can display correctly; these providers may log basic request data (e.g. IP address) but we do not receive that data.",
      "Your browser may still store small technical items in local storage to make the site work; these do not identify you personally.",
    ],
  },
  {
    id: "contact",
    title: "10. Contact us",
    body: [
      "If you have any questions about this policy or want to exercise your data-protection rights, please contact:",
    ],
    list: [
      `Email: ${SITE.email}`,
      `WhatsApp: ${SITE.whatsappPretty}`,
      `Location: ${SITE.location}`,
    ],
  },
  {
    id: "changes",
    title: "11. Changes to this policy",
    body: [
      "We may update this policy from time to time. The date at the top of this page shows when it was last updated. Any material changes will be highlighted on the website.",
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = `Privacy Policy · ${SITE.name}`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="privacy-page" className="relative bg-[#0A0A0A] text-white min-h-screen">
      <Navbar />

      <section className="pt-32 sm:pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FBBF24] transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back home
          </Link>

          <p className="tag-label mb-5">// Legal / Privacy</p>
          <h1
            data-testid="privacy-heading"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white"
          >
            Privacy <span className="text-[#FBBF24]">Policy.</span>
          </h1>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Effective {EFFECTIVE_DATE}
          </p>

          <p className="mt-10 text-neutral-300 leading-relaxed">
            This policy explains what personal information{" "}
            <span className="text-white">{SITE.name}</span> collects when you
            use this website or get in touch with us, how we use it, and the
            rights you have under UK data-protection law (UK GDPR and the Data
            Protection Act 2018).
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="space-y-10">
            {SECTIONS.map((s) => (
              <div key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`}>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
                  {s.title}
                </h2>
                {(s.body || []).map((p, i) => (
                  <p
                    key={i}
                    className="text-neutral-300 leading-relaxed mb-4 last:mb-0"
                  >
                    {p}
                  </p>
                ))}
                {s.list && s.list.length > 0 && (
                  <ul className="mt-2 space-y-2 list-none">
                    {s.list.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-neutral-300 leading-relaxed"
                      >
                        <span className="mt-2 w-1.5 h-1.5 bg-[#FBBF24] shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(s.body_after || []).map((p, i) => (
                  <p
                    key={`ba-${i}`}
                    className="text-neutral-300 leading-relaxed mt-4"
                  >
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-600">
              © {new Date().getFullYear()} {SITE.name} · {SITE.domain}
            </p>
            <Link
              to="/terms"
              data-testid="privacy-terms-link"
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#FBBF24] transition-colors"
            >
              Read our Terms of Service →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
